<template>
    <div>
        <modal-generate-integration-key @generated="handleKeysRefresh" />
        <modal-integration-agents ref="modalAgents" />

        <b-modal
            id="modal-edit-key-description"
            shadow
            bg-variant="white"
            backdrop
            hide-footer
            centered
            @hidden="onDescriptionModalHidden"
        >
            <template #modal-header>
                <div class="d-flex align-items-center justify-content-start w-100 title-modal">
                    <span class="modal-title">{{ $t("IntegrationKeys.EditDescriptionModal") }}</span>
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    class="close"
                    @click="closeDescriptionModal"
                >
                    <CloseModalIcon />
                </button>
            </template>
            <div class="modal-integration-key__container">
                <b-form-group
                    label-for="ik-desc-edit-input"
                    :label="$t('IntegrationKeys.Description')"
                    :invalid-feedback="descriptionEditInvalidFeedback"
                >
                    <b-form-input
                        id="ik-desc-edit-input"
                        v-model.trim="descriptionDraft"
                        :state="descriptionDraftState"
                        :placeholder="$t('TypeHere')"
                        maxlength="250"
                    />
                </b-form-group>
                <div class="modal-integration-key__footer d-flex justify-content-end flex-wrap gap-1">
                    <sys-button
                        scheme="square"
                        variant="outline"
                        size="md"
                        :disabled="savingDescription"
                        @click="closeDescriptionModal"
                    >
                        {{ $t("IntegrationKeys.Cancel") }}
                    </sys-button>
                    <sys-button
                        v-if="!savingDescription"
                        scheme="square"
                        variant="contained"
                        size="md"
                        @click="saveDescription"
                    >
                        {{ $t("IntegrationKeys.Save") }}
                    </sys-button>
                </div>
            </div>
        </b-modal>

        <div id="card-integration-keys">
            <b-alert
                v-if="missingAccount"
                show
                variant="warning"
                class="mb-3"
            >
                {{ $t("IntegrationKeys.MissingAccount") }}
            </b-alert>

            <div>
                <div class="d-flex align-items-center justify-content-start c-header">
                    <div
                        class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-sm-between c-header-content"
                    >
                        <span class="c-title">{{ $t("IntegrationKeys.Title") }}</span>
                        <b-button
                            class="btn-new-key"
                            :disabled="missingAccount"
                            @click="openGenerateModal"
                        >
                            <span class="text-nowrap">{{ $t("IntegrationKeys.Generate") }}</span>
                        </b-button>
                    </div>
                </div>
                <div class="mt-1 table-integration-keys">
                    <table-default
                        :rows="keysList"
                        :fields="fields"
                        :stickyHeader="false"
                        :striped="true"
                        :filter="filter"
                        id="table-integration-keys"
                        @updatePageNumber="updatePageNumber"
                        @updatepageSize="updatepageSize"
                        currentPageOff
                    >
                        <template v-slot:cell(description)="data">
                            <div
                                class="d-flex align-items-center justify-content-center ik-desc-cell"
                            >
                                <span
                                    :id="`tooltip-ik-desc-${data.index}`"
                                    class="text-truncate ik-desc-text mr-1"
                                >
                                    {{ data.item.description }}
                                </span>
                                <b-tooltip
                                    v-if="data.item.description"
                                    custom-class="tooltip-integration-keys"
                                    triggers="hover blur"
                                    :target="`tooltip-ik-desc-${data.index}`"
                                    placement="top"
                                >
                                    <div class="info-tooltip-integration-keys">
                                        {{ data.item.description }}
                                    </div>
                                </b-tooltip>
                                <b-button
                                    variant="link"
                                    class="p-0 ik-icon-btn flex-shrink-0"
                                    :disabled="missingAccount"
                                    v-b-tooltip.hover
                                    :title="$t('IntegrationKeys.EditDescriptionTooltip')"
                                    @click="openDescriptionModal(data.item)"
                                >
                                    <EditIcon />
                                </b-button>
                            </div>
                        </template>
                        <template v-slot:cell(agentsCount)="data">
                            <div
                                class="d-flex align-items-center justify-content-center ik-agents-cell"
                            >
                                <span class="ik-agents-count">{{ data.item.agentsCount }}</span>
                                <b-button
                                    variant="link"
                                    class="p-0 ik-icon-btn ik-agents-eye"
                                    v-b-tooltip.hover
                                    :title="$t('IntegrationKeys.ViewAgents')"
                                    @click="openAgentsModal(data.item)"
                                >
                                    <EyeIconLarge />
                                </b-button>
                            </div>
                        </template>
                        <template v-slot:cell(active)="data">
                            <div class="d-flex align-items-center ik-active-row">
                                <div
                                    class="d-flex justify-content-center align-items-center flex-grow-1 ik-active-switch-wrap"
                                >
                                    <b-form-checkbox
                                        switch
                                        class="ik-integration-switch mb-0"
                                        :checked="data.item.active"
                                        :disabled="
                                            missingAccount || togglingActiveId === data.item.id
                                        "
                                        @input="(val) => handleActiveToggle(data.item, val)"
                                    >
                                        {{
                                            data.item.active
                                                ? $t("IntegrationKeys.Active")
                                                : $t("IntegrationKeys.Inactive")
                                        }}
                                    </b-form-checkbox>
                                </div>
                                <b-button
                                    variant="link"
                                    class="p-0 ik-icon-btn ik-delete-btn flex-shrink-0"
                                    v-b-tooltip.hover
                                    :title="$t('IntegrationKeys.Delete')"
                                    @click="confirmDelete(data.item.id)"
                                >
                                    <DeleteIcon />
                                </b-button>
                            </div>
                        </template>
                    </table-default>
                </div>
                <div class="demo-spacing-0">
                    <b-alert
                        variant="primary"
                        :show="!missingAccount && keysList.length === 0 && !listLoading"
                    >
                        <div class="alert-body">
                            <span>{{ $t("NoResultsFound") }}</span>
                        </div>
                    </b-alert>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        BAlert,
        BButton,
        BTooltip,
        VBTooltip,
        BModal,
        BFormGroup,
        BFormInput,
        BFormCheckbox
    } from "bootstrap-vue";
    import TableDefault from "@core/components/table-default/TableDefault.vue";
    import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
    import CloseModalIcon from "@/@core/assets/icons/close-modal-icon.svg";
    import SysButton from "@/@core/components/sys-button";
    import EditIcon from "@/assets/images/pages/edit-icon-grey.svg";
    import DeleteIcon from "@/assets/images/pages/trash-icon-grey.svg";
    import ModalGenerateIntegrationKey from "./components/ModalGenerateIntegrationKey.vue";
    import ModalIntegrationAgents from "./components/ModalIntegrationAgents.vue";
    import { mapActions, mapGetters, mapMutations } from "vuex";

    export default {
        name: "IntegrationKeysPage",
        directives: {
            "b-tooltip": VBTooltip
        },
        components: {
            BAlert,
            BButton,
            BTooltip,
            BModal,
            BFormGroup,
            BFormInput,
            BFormCheckbox,
            TableDefault,
            ModalGenerateIntegrationKey,
            ModalIntegrationAgents,
            EditIcon,
            DeleteIcon,
            CloseModalIcon,
            SysButton,
            EyeIconLarge: () => import("@core/assets/icons/eye-icon-large.svg")
        },
        data() {
            return {
                descriptionMaxLength: 250,
                keysList: [],
                listLoading: false,
                togglingActiveId: null,
                descriptionEditId: null,
                descriptionDraft: "",
                descriptionDraftState: null,
                savingDescription: false,
                fields: [
                    {
                        label: "IntegrationKeys.Description",
                        key: "description",
                        sortable: false,
                        thClass: "th col-description text-center",
                        tdClass: "td text-center ik-td-cell col-description"
                    },
                    {
                        label: "IntegrationKeys.AgentsCount",
                        key: "agentsCount",
                        sortable: false,
                        thClass: "th col-agents text-center",
                        tdClass: "td text-center ik-td-cell col-agents"
                    },
                    {
                        label: "IntegrationKeys.Active",
                        key: "active",
                        sortable: false,
                        thClass: "th col-active-switch text-center",
                        tdClass: "td ik-td-cell ik-td-active col-active-switch"
                    }
                ],
                filter: {
                    pageNumber: 1,
                    pageSize: 10,
                    totalItems: 0
                }
            };
        },
        computed: {
            ...mapGetters("auth", ["getAccountId"]),
            missingAccount() {
                return this.getAccountId == null;
            },
            descriptionEditInvalidFeedback() {
                const d = this.descriptionDraft || "";
                if (!d.trim()) {
                    return this.$t("IntegrationKeys.RequiredDescription");
                }
                if (d.length > this.descriptionMaxLength) {
                    return this.$t("IntegrationKeys.DescriptionMaxLength");
                }
                return this.$t("IntegrationKeys.RequiredDescription");
            }
        },
        async mounted() {
            await this.fetchPermissions();
            await this.loadKeys();
        },
        methods: {
            ...mapActions("auth", ["fetchPermissions"]),
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            updatepageSize(value) {
                this.filter.pageSize = value;
            },
            updatePageNumber(value) {
                this.filter.pageNumber = value;
            },
            showToast(title, icon, text, variant) {
                this.$toast({
                    component: ToastificationContent,
                    props: { title, icon, text, variant }
                });
            },
            openGenerateModal() {
                this.$root.$emit("bv::show::modal", "modal-generate-integration-key");
            },
            openAgentsModal(row) {
                this.$refs.modalAgents?.open(row.integrationAgents || [], row.description);
            },
            openDescriptionModal(row) {
                this.descriptionEditId = row.id;
                this.descriptionDraft = row.description || "";
                this.descriptionDraftState = null;
                this.$bvModal.show("modal-edit-key-description");
            },
            closeDescriptionModal() {
                this.$bvModal.hide("modal-edit-key-description");
            },
            onDescriptionModalHidden() {
                this.descriptionEditId = null;
                this.descriptionDraft = "";
                this.descriptionDraftState = null;
                this.savingDescription = false;
            },
            async saveDescription() {
                const len = this.descriptionDraft?.length ?? 0;
                const ok =
                    !!this.descriptionDraft?.trim() && len <= this.descriptionMaxLength;
                this.descriptionDraftState = ok;
                if (!ok) return;
                if (this.descriptionEditId == null || this.missingAccount) return;

                this.savingDescription = true;
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                try {
                    const response = await this.$httpMyConnect.put(
                        `/api/integration-agents/keys/${this.descriptionEditId}`,
                        { description: this.descriptionDraft.trim() },
                        { params: { accountId: this.getAccountId } }
                    );
                    const body = response.data;
                    if (body.success === false) {
                        this.showToast(
                            this.$t("IntegrationKeys.Error"),
                            "FrownIcon",
                            body.message || this.$t("IntegrationKeys.LoadError"),
                            "danger"
                        );
                        return;
                    }
                    const row = this.keysList.find((k) => k.id === this.descriptionEditId);
                    if (row) {
                        this.$set(row, "description", this.descriptionDraft.trim());
                    }
                    this.showToast(
                        this.$t("IntegrationKeys.Success"),
                        "SmileIcon",
                        this.$t("IntegrationKeys.UpdateSuccess"),
                        "success"
                    );
                    this.closeDescriptionModal();
                } catch (error) {
                    const msg =
                        error.response?.data?.message ||
                        error.message ||
                        this.$t("IntegrationKeys.LoadError");
                    this.showToast(this.$t("IntegrationKeys.Error"), "FrownIcon", msg, "danger");
                } finally {
                    this.savingDescription = false;
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            },
            async handleKeysRefresh() {
                await this.loadKeys();
            },
            async handleActiveToggle(item, newVal) {
                if (this.missingAccount || item == null) return;
                if (item.active === newVal) return;
                if (this.togglingActiveId === item.id) return;

                const prev = item.active;
                this.togglingActiveId = item.id;
                this.$set(item, "active", newVal);

                try {
                    const response = await this.$httpMyConnect.put(
                        `/api/integration-agents/keys/${item.id}`,
                        { active: newVal },
                        { params: { accountId: this.getAccountId } }
                    );
                    const body = response.data;
                    if (body.success === false) {
                        this.$set(item, "active", prev);
                        this.showToast(
                            this.$t("IntegrationKeys.Error"),
                            "FrownIcon",
                            body.message || this.$t("IntegrationKeys.LoadError"),
                            "danger"
                        );
                        return;
                    }
                    this.showToast(
                        this.$t("IntegrationKeys.Success"),
                        "SmileIcon",
                        this.$t("IntegrationKeys.UpdateSuccess"),
                        "success"
                    );
                } catch (error) {
                    this.$set(item, "active", prev);
                    const msg =
                        error.response?.data?.message ||
                        error.message ||
                        this.$t("IntegrationKeys.LoadError");
                    this.showToast(this.$t("IntegrationKeys.Error"), "FrownIcon", msg, "danger");
                } finally {
                    this.togglingActiveId = null;
                }
            },
            async loadKeys() {
                if (this.missingAccount) {
                    this.keysList = [];
                    return;
                }
                this.listLoading = true;
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                try {
                    const response = await this.$httpMyConnect.get("/api/integration-agents/keys", {
                        params: { accountId: this.getAccountId }
                    });
                    const body = response.data;
                    if (body.success === false) {
                        this.showToast(
                            this.$t("IntegrationKeys.Error"),
                            "FrownIcon",
                            body.message || this.$t("IntegrationKeys.LoadError"),
                            "danger"
                        );
                        this.keysList = [];
                        return;
                    }
                    const raw = body.data || [];
                    const rows = raw.map((item) => ({
                        ...item,
                        agentsCount: item.integrationAgents?.length ?? 0
                    }));
                    this.keysList = rows;
                    this.filter = {
                        pageNumber: 1,
                        pageSize: this.filter.pageSize,
                        totalItems: rows.length
                    };
                } catch (error) {
                    const msg =
                        error.response?.data?.message ||
                        error.message ||
                        this.$t("IntegrationKeys.LoadError");
                    this.showToast(this.$t("IntegrationKeys.Error"), "FrownIcon", msg, "danger");
                    this.keysList = [];
                } finally {
                    this.listLoading = false;
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            },
            confirmDelete(id) {
                if (id == null) return;
                this.$swal({
                    title: this.$t("IntegrationKeys.Attention"),
                    showCloseButton: true,
                    showCancelButton: true,
                    cancelButtonText: this.$t("IntegrationKeys.Cancel"),
                    confirmButtonText: this.$t("IntegrationKeys.Delete"),
                    text: this.$t("IntegrationKeys.DeleteConfirm"),
                    customClass: {
                        container: "swal-default",
                        confirmButton: "btn btn-primary",
                        cancelButton: "btn btn-outline-danger ml-1"
                    },
                    buttonsStyling: false
                }).then(async (result) => {
                    if (!result.isConfirmed) return;
                    await this.deleteKey(id);
                });
            },
            async deleteKey(id) {
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                try {
                    const response = await this.$httpMyConnect.delete(
                        `/api/integration-agents/keys/${id}`,
                        { params: { accountId: this.getAccountId } }
                    );
                    const body = response.data;
                    if (body.success === false) {
                        this.showToast(
                            this.$t("IntegrationKeys.Error"),
                            "FrownIcon",
                            body.message || this.$t("IntegrationKeys.LoadError"),
                            "danger"
                        );
                        return;
                    }
                    this.showToast(
                        this.$t("IntegrationKeys.Success"),
                        "SmileIcon",
                        this.$t("IntegrationKeys.DeleteSuccess"),
                        "success"
                    );
                    await this.loadKeys();
                } catch (error) {
                    const msg =
                        error.response?.data?.message ||
                        error.message ||
                        this.$t("IntegrationKeys.LoadError");
                    this.showToast(this.$t("IntegrationKeys.Error"), "FrownIcon", msg, "danger");
                } finally {
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            }
        }
    };
</script>

<style lang="scss">
    @import "~@core/scss/vue/libs/vue-sweetalert.scss";

    #modal-edit-key-description {
        .modal-dialog {
            width: 95%;
            max-width: 560px;
        }

        .modal-content {
            width: 100%;
            right: 0 !important;
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid #e0e0e0;
            box-shadow: 0px 8px 32px rgba(58, 46, 38, 0.12);
        }

        .modal-header {
            position: relative !important;
            display: flex;
            align-items: center;
            height: 66px;
            background-color: #eceff1;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom: 1px solid #e0e0e0;

            .title-modal {
                height: 100%;
            }

            .modal-title {
                color: #4c4541;
                font-size: 18px;
                font-weight: 600;
                line-height: 26px;
            }

            .close {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                min-width: 28px;
                min-height: 28px;
                max-width: 28px;
                max-height: 28px;
                padding: 0;
                margin: 0;
                opacity: 1;
                transform: translate(6px, 5px);
                border: 1px solid #974900 !important;
                border-radius: 5px;
                background: transparent !important;
                box-shadow: none !important;

                svg {
                    min-height: 12px;
                    min-width: 12px;
                    max-width: 12px;
                    max-height: 12px;
                    fill: #974900 !important;
                }

                svg path {
                    fill: #974900 !important;
                }

                &:hover {
                    opacity: 1;
                    transform: translate(6px, 5px);
                    background-color: #ffede2 !important;
                }

                &:active {
                    background-color: #ffdbc4 !important;
                }

                &:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.15rem rgba(151, 73, 0, 0.25);
                }
            }
        }

        .modal-body {
            padding: 0 !important;
        }

        .modal-integration-key__container {
            display: flex;
            flex-direction: column;
            padding: 20px 16px;
            gap: 20px;
            background-color: #fff;

            .form-group {
                margin: 0;
            }

            label {
                font-weight: 400;
                font-size: 12px;
                line-height: 16px;
                color: #4c4541;
                padding-bottom: 0 !important;
                margin-bottom: 4px;
            }

            .form-control {
                color: #4c4541;
                border-color: #d5c8ba;
                border-radius: 6px;
                font-size: 14px;
                line-height: 20px;

                &::placeholder {
                    color: #cfc4be;
                }

                &:focus {
                    border-color: #974900;
                    box-shadow: none;
                }
            }

            .form-control.is-invalid {
                background-image: none !important;
                border-color: #dc3545;
            }

            .invalid-feedback {
                margin-left: 5px !important;
                font-size: 10px;
            }

            .modal-integration-key__footer {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                margin-top: 0;
                gap: 8px;
            }
        }
    }

    #card-integration-keys {
        padding: 16px;
        width: 100%;
        border-radius: 6px;
        background: #fff;
        box-shadow: 0px 4px 24px 0px rgba(58, 46, 38, 0.1);

        @media (max-width: 768px) {
            margin-top: 24px;
        }

        .c-header {
            width: 100%;
            display: flex;
            margin-bottom: 7px;

            .c-header-content {
                width: 100%;
            }

            .c-title {
                font-weight: 600;
                font-size: 18px;
                line-height: 26px;
                color: #4c4541 !important;
            }

            .btn-new-key {
                background: #974900 !important;
                border: none;
                padding: 4px 14px !important;

                span {
                    font-weight: 600;
                    font-size: 12px;
                    line-height: 20px;
                    letter-spacing: 0.0125em;
                }
            }
        }

        .table-integration-keys {
            overflow-x: auto;
            margin-top: 0 !important;
            padding-top: 8px !important;

            /* Busca: mesma largura útil da tabela (a tabela não herda padding de col); espaço antes da tabela */
            .custom-search.col-12 {
                flex: 0 0 100%;
                max-width: 100%;
                padding-left: 0 !important;
                padding-right: 0 !important;
                margin-bottom: 20px;
            }

            .custom-search .d-flex {
                width: 100%;
            }

            .custom-search .input-search {
                width: 100% !important;
                min-width: 0;
            }

            /* Larguras: descrição ocupa o restante; agentes e ativa só o necessário (TableDefault força th width 1% em todas) */
            #table-default thead th.col-description {
                width: auto !important;
                min-width: 200px !important;
            }

            #table-default thead th.col-agents {
                width: 1% !important;
                min-width: 0 !important;
                max-width: 120px !important;
                white-space: nowrap !important;
            }

            #table-default thead th.col-active-switch {
                width: 1% !important;
                min-width: 0 !important;
                white-space: nowrap !important;
            }

            #table-default tbody td.col-agents,
            #table-default tbody td.col-active-switch {
                width: 1% !important;
                white-space: nowrap !important;
            }

            #table-default table {
                width: 100% !important;
            }

            .row {
                margin: 0;
            }

            .ik-desc-cell {
                min-width: 0;
                width: 100%;
            }

            .ik-desc-text {
                max-width: calc(100% - 36px);
                text-align: center;
            }

            .ik-agents-cell {
                gap: 6px;
            }

            .ik-agents-count {
                flex: 0 0 2.5rem;
                width: 2.5rem;
                text-align: right;
                font-variant-numeric: tabular-nums;
                line-height: 1;
            }

            .ik-agents-eye.btn {
                flex-shrink: 0;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                width: 2.75rem;
                height: 2.75rem;
                min-width: 2.75rem;
                min-height: 2.75rem;
                padding: 0 !important;
                line-height: 0 !important;
                vertical-align: middle;

                svg {
                    display: block;
                    width: 18px !important;
                    height: 18px !important;
                    margin: 0;
                    flex-shrink: 0;
                }
            }

            tbody tr td.ik-td-cell {
                vertical-align: middle !important;
            }

            .ik-icon-btn {
                line-height: 1;
                min-width: 28px;
                min-height: 28px;
                display: inline-flex;
                align-items: center;
                justify-content: center;

                svg {
                    width: 18px;
                    height: 18px;
                }

                &:hover {
                    background-color: rgba(151, 73, 0, 0.08) !important;
                    border-radius: 4px;
                }
            }

            .ik-delete-btn:hover svg path {
                fill: #c00;
            }

            /* Sobrescreve hacks do TableDefault (#table-default) que quebram o switch */
            #table-default tbody tr td .ik-integration-switch.custom-switch {
                display: inline-flex !important;
                align-items: center !important;
                padding-left: 0 !important;
                margin: 0 !important;
                min-height: 0 !important;

                .custom-control-label {
                    position: relative;
                    margin-bottom: 0 !important;
                    padding-left: 4rem !important;
                    padding-top: 0 !important;
                    font-size: 13px;
                    font-weight: 500;
                    color: #4c4541;
                    line-height: 1.5rem !important;
                    cursor: pointer;
                    text-align: left;

                    &::before {
                        position: absolute !important;
                        top: 50% !important;
                        left: 0 !important;
                        width: 3rem !important;
                        height: 1.5rem !important;
                        margin: 0 !important;
                        transform: translateY(-50%) !important;
                        border-radius: 0.75rem !important;
                        background-color: #d8d2cc !important;
                        border: none !important;
                        box-shadow: inset 0 1px 2px rgba(58, 46, 38, 0.08) !important;
                    }

                    &::after {
                        position: absolute !important;
                        top: 50% !important;
                        left: 0.25rem !important;
                        width: 1.125rem !important;
                        height: 1.125rem !important;
                        margin: 0 !important;
                        transform: translate(0, -50%) !important;
                        border-radius: 50% !important;
                        background-color: #fff !important;
                        box-shadow: 0 1px 3px rgba(58, 46, 38, 0.22) !important;
                    }
                }

                .custom-control-input:checked ~ .custom-control-label::before {
                    background-color: #974900 !important;
                    box-shadow: none !important;
                }

                .custom-control-input:checked ~ .custom-control-label::after {
                    left: 0.25rem !important;
                    transform: translate(1.35rem, -50%) !important;
                }

                .custom-control-input:focus ~ .custom-control-label::before {
                    box-shadow: 0 0 0 0.15rem rgba(151, 73, 0, 0.35) !important;
                }

                .custom-control-input:checked:focus ~ .custom-control-label::before {
                    box-shadow: 0 0 0 0.15rem rgba(151, 73, 0, 0.35) !important;
                }

                .custom-control-input:disabled ~ .custom-control-label {
                    opacity: 0.55;
                    cursor: not-allowed;
                }
            }

            tbody tr td.ik-td-active {
                text-align: left !important;
                padding-right: 12px !important;
            }

            .ik-active-row {
                width: 100%;
                max-width: 100%;
                min-width: 0;
                gap: 8px;
                flex-wrap: nowrap;
            }

            .ik-active-switch-wrap {
                min-width: 0;
                flex: 0 1 auto;
            }

            table {
                thead th {
                    padding: 10.5px 9px !important;
                    text-align: center !important;
                    vertical-align: middle !important;
                    background-color: rgba(151, 73, 0, 0.05) !important;
                }

                thead tr th div.text-nowrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    text-transform: uppercase;
                    font-size: 12px;
                }

                td {
                    padding: 13.5px 10px !important;
                    text-align: center !important;
                    vertical-align: middle !important;
                    border: 1px solid #e0e0e0 !important;
                    border-bottom: none !important;
                    border-left: none !important;
                    border-right: none !important;
                }

                tbody td.col-description {
                    min-width: 0;
                }
            }
        }
    }

    .tooltip-integration-keys {
        .tooltip-inner {
            padding: 8px;
            max-width: 360px;
            text-align: left;
        }

        .info-tooltip-integration-keys {
            font-size: 14px;
            line-height: 20px;
        }
    }

    @media (max-width: 575px) {
        #card-integration-keys {
            .c-header {
                .btn-new-key {
                    width: 100%;
                    margin-top: 16px;
                }
            }
        }
    }
</style>
